/**
 * Résout les clusters hreflang FR / EN / AR pour URLs SEO distinctes (pas cookie i18n).
 */

import { BLOG_POSTS_SEO } from '@/lib/data/blog-posts-seo';
import { GUIDE_PAGES, BLOG_POSTS_EXPANDED, FAQ_PAGES } from '@/lib/seo/content-network';
import { cityHreflangCluster, isCityLandingSlug, SEO_BASE_URL } from '@/lib/seo/city-urls';

const BASE = SEO_BASE_URL;

/** Extrait le pathname absolu depuis une URL complète ou un chemin. */
export const toCanonicalPath = (urlOrPath: string): string => {
  try {
    if (urlOrPath.startsWith('http')) {
      return new URL(urlOrPath).pathname || '/';
    }
  } catch {
    /* fall through */
  }
  const p = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
  return p || '/';
};

const frStrip = (path: string): string | null => {
  if (!path.startsWith('/fr/')) return null;
  return path.slice(3) || '/';
};

const enStrip = (path: string): string | null => {
  if (!path.startsWith('/en/')) return null;
  return path.slice(3) || '/';
};

const arStrip = (path: string): string | null => {
  if (!path.startsWith('/ar/')) return null;
  return path.slice(3) || '/';
};

const prefixFr = (sub: string) => {
  const s = sub.startsWith('/') ? sub : `/${sub}`;
  return `/fr${s}`;
};

const prefixEn = (sub: string) => {
  const s = sub.startsWith('/') ? sub : `/${sub}`;
  return `/en${s}`;
};

const prefixAr = (sub: string) => {
  const s = sub.startsWith('/') ? sub : `/${sub}`;
  return `/ar${s}`;
};

const mapFrSubToEnSub = (sub: string): string | null => {
  if (sub === '/location-voiture') return '/car-rental';
  if (sub === '/location-voiture/longue-duree') return '/car-rental/long-term';
  if (sub === '/location-voiture/pas-cher') return '/car-rental/cheap';
  if (sub === '/location-voiture/luxe') return '/car-rental/luxury';

  let m = sub.match(/^\/location-voiture\/aeroport\/([^/]+)$/);
  if (m) return `/car-rental/airport/${m[1]}`;

  m = sub.match(/^\/location-voiture\/categorie\/([^/]+)$/);
  if (m) return `/car-rental/category/${m[1]}`;

  m = sub.match(/^\/location-voiture\/([^/]+)\/([^/]+)$/);
  if (m) return `/car-rental/${m[1]}/${m[2]}`;

  m = sub.match(/^\/location-voiture\/([^/]+)$/);
  if (m) return `/car-rental/${m[1]}`;

  return null;
};

const mapEnSubToFrSub = (sub: string): string | null => {
  if (sub === '/car-rental') return '/location-voiture';
  if (sub === '/car-rental/long-term') return '/location-voiture/longue-duree';
  if (sub === '/car-rental/cheap') return '/location-voiture/pas-cher';
  if (sub === '/car-rental/luxury') return '/location-voiture/luxe';

  let m = sub.match(/^\/car-rental\/airport\/([^/]+)$/);
  if (m) return `/location-voiture/aeroport/${m[1]}`;

  m = sub.match(/^\/car-rental\/category\/([^/]+)$/);
  if (m) return `/location-voiture/categorie/${m[1]}`;

  m = sub.match(/^\/car-rental\/([^/]+)\/([^/]+)$/);
  if (m) return `/location-voiture/${m[1]}/${m[2]}`;

  m = sub.match(/^\/car-rental\/([^/]+)$/);
  if (m) return `/location-voiture/${m[1]}`;

  return null;
};

/** AR shares FR subpath structure under /ar/. */
const mapFrSubToArSub = (sub: string): string | null => sub;

const frCanonicalAbsolute = (frSub: string): string => {
  const cityMatch = frSub.match(/^\/location-voiture\/([^/]+)$/);
  if (cityMatch && isCityLandingSlug(cityMatch[1])) {
    return `${BASE}${frSub}`;
  }
  return `${BASE}${prefixFr(frSub)}`;
};

const arCanonicalAbsolute = (arSub: string): string => {
  const cityMatch = arSub.match(/^\/location-voiture\/([^/]+)$/);
  if (cityMatch && isCityLandingSlug(cityMatch[1])) {
    return `${BASE}${prefixAr(arSub)}`;
  }
  return `${BASE}${prefixAr(arSub)}`;
};

const mapUnprefixedFrCity = (path: string): CityHreflangCluster | null => {
  const m = path.match(/^\/location-voiture\/([^/]+)$/);
  if (!m || !isCityLandingSlug(m[1])) return null;
  return cityHreflangCluster(m[1]);
};

export interface HreflangCluster {
  fr: string;
  /** Absent quand aucune URL anglaise distincte ne doit être annoncée. */
  en?: string;
  /** Absent tant qu’il n’existe pas de page AR équivalente (blog, FAQ, guides). */
  ar?: string;
  arMa?: string;
  xDefault: string;
}

/** @deprecated Use HreflangCluster */
export type HreflangTriple = HreflangCluster;

const toCluster = (fr: string, en: string, ar: string): HreflangCluster => ({
  fr,
  en,
  ar,
  arMa: ar,
  xDefault: fr,
});

/** Guides : FR + EN uniquement (pas d’URL AR dédiée). */
const toClusterFrEn = (fr: string, en: string): HreflangCluster => ({
  fr,
  en,
  xDefault: fr,
});

/** Blog / FAQ : FR + EN ; AR pointe vers l’URL FR tant qu’il n’y a pas de slug /ar/ dédié. */
const toClusterFrEnWithArFallback = (fr: string, en: string): HreflangCluster => ({
  fr,
  en,
  ar: fr,
  arMa: fr,
  xDefault: fr,
});

const mapProgrammaticPair = (path: string): HreflangCluster | null => {
  const unprefixedCity = mapUnprefixedFrCity(path);
  if (unprefixedCity) return unprefixedCity;

  const frS = frStrip(path);
  if (frS) {
    const enSub = mapFrSubToEnSub(frS);
    const arSub = mapFrSubToArSub(frS);
    if (!enSub || !arSub) return null;
    return toCluster(
      frCanonicalAbsolute(frS),
      `${BASE}${prefixEn(enSub)}`,
      arCanonicalAbsolute(arSub)
    );
  }

  const enS = enStrip(path);
  if (enS) {
    const frSub = mapEnSubToFrSub(enS);
    if (!frSub) return null;
    const cityMatch = frSub.match(/^\/location-voiture\/([^/]+)$/);
    if (cityMatch && isCityLandingSlug(cityMatch[1])) {
      return cityHreflangCluster(cityMatch[1]);
    }
    const arSub = mapFrSubToArSub(frSub);
    if (!arSub) return null;
    return toCluster(
      `${BASE}${prefixFr(frSub)}`,
      `${BASE}${path}`,
      arCanonicalAbsolute(arSub)
    );
  }

  const arS = arStrip(path);
  if (arS) {
    const enSub = mapFrSubToEnSub(arS);
    const frSub = mapEnSubToFrSub(enSub ?? '');
    if (!enSub) return null;
    const cityMatch = arS.match(/^\/location-voiture\/([^/]+)$/);
    if (cityMatch && isCityLandingSlug(cityMatch[1])) {
      return cityHreflangCluster(cityMatch[1]);
    }
    if (!frSub) return null;
    const frAbs =
      frSub.match(/^\/location-voiture\/([^/]+)$/) && isCityLandingSlug(frSub.split('/').pop() ?? '')
        ? `${BASE}${frSub}`
        : `${BASE}${prefixFr(frSub)}`;
    return toCluster(frAbs, `${BASE}${prefixEn(enSub)}`, `${BASE}${path}`);
  }

  return null;
};

const mapGuidePair = (path: string): HreflangCluster | null => {
  const m = path.match(/^\/guide\/([^/]+)\/?$/);
  if (!m) return null;
  const slug = m[1];
  const guide = GUIDE_PAGES.find(g => g.slug === slug || g.slugEn === slug);
  if (!guide) return null;
  const fr = `${BASE}/guide/${guide.slug}`;
  const en = `${BASE}/guide/${guide.slugEn}`;
  return toClusterFrEn(fr, en);
};

/**
 * Articles dont le HTML initial est français uniquement.
 * Ne pas pointer `en` / `ar` vers le même document, ni vers une traduction
 * qui n’a pas encore son propre HTML initial.
 */
const FRENCH_ONLY_INITIAL_HTML_BLOG_SLUGS = new Set([
  'location-longue-duree-maroc',
  'prix-location-voiture-maroc',
]);

const frenchOnlyBlogCluster = (slug: string): HreflangCluster => {
  const url = `${BASE}/blog/${slug}`;
  return { fr: url, xDefault: url };
};

const mapBlogPair = (path: string): HreflangCluster | null => {
  const m = path.match(/^\/blog\/([^/]+)\/?$/);
  if (!m) return null;
  const slug = m[1];
  if (FRENCH_ONLY_INITIAL_HTML_BLOG_SLUGS.has(slug)) {
    return frenchOnlyBlogCluster(slug);
  }
  const expanded = BLOG_POSTS_EXPANDED.find(p => p.slug === slug || p.slugEn === slug);
  if (expanded) {
    return toClusterFrEnWithArFallback(`${BASE}/blog/${expanded.slug}`, `${BASE}/blog/${expanded.slugEn}`);
  }
  const legacy = BLOG_POSTS_SEO.find(p => p.slug === slug || p.id === slug);
  if (legacy) {
    const url = `${BASE}/blog/${legacy.slug}`;
    return toClusterFrEnWithArFallback(url, url);
  }
  return null;
};

const mapFaqPair = (path: string): HreflangCluster | null => {
  const m = path.match(/^\/faq\/([^/]+)\/?$/);
  if (!m) return null;
  const slug = m[1];
  const page = FAQ_PAGES.find(p => p.slug === slug || p.slugEn === slug);
  if (!page) return null;
  return toClusterFrEnWithArFallback(`${BASE}/faq/${page.slug}`, `${BASE}/faq/${page.slugEn}`);
};

/**
 * Accueil : une seule URL française. `en` et `ar` ne sont pas annoncés
 * tant qu’il n’existe pas de page d’accueil traduite distincte.
 */
const mapHomepageCluster = (path: string): HreflangCluster | null => {
  if (path !== '/' && path !== '') return null;
  const home = `${BASE}/`;
  return { fr: home, xDefault: home };
};

/**
 * @param canonicalUrl URL absolue canonique de la page courante
 */
export const resolveHreflangCluster = (canonicalUrl: string): HreflangCluster | null => {
  const path = toCanonicalPath(canonicalUrl);
  const fromHome = mapHomepageCluster(path);
  if (fromHome) return fromHome;
  const fromProgrammatic = mapProgrammaticPair(path);
  if (fromProgrammatic) return fromProgrammatic;
  const fromGuide = mapGuidePair(path);
  if (fromGuide) return fromGuide;
  const fromBlog = mapBlogPair(path);
  if (fromBlog) return fromBlog;
  const fromFaq = mapFaqPair(path);
  if (fromFaq) return fromFaq;
  return null;
};

/** @deprecated Use resolveHreflangCluster */
export const resolveHreflangTriple = resolveHreflangCluster;
